import { type MutateOptions, useMutation } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { useCallback } from 'react';
import { useEffectiveExperience } from '@/features/experiences/contexts/PageExperienceContext';
import { gqlClient } from '@/lib';
import type { LabelType, Label } from '@/types/gql.generated';
import type { QueryError } from '@/utils/errors';
import type {
  CreateExperienceLabelMutation,
  CreateExperienceLabelMutationVariables,
} from './useCreateLabel.generated';
import { useLabelCache } from './useLabelCache';

const query = gql`
  mutation CreateExperienceLabel($input: CreateExperienceLabelInput!) {
    createExperienceLabel(input: $input) {
      id
      text
      sortOrder
    }
  }
`;

export const useCreateLabel = (labelType: LabelType) => {
  const experience = useEffectiveExperience();
  const cache = useLabelCache();

  const { mutate, isPending } = useMutation<
    CreateExperienceLabelMutation,
    QueryError,
    CreateExperienceLabelMutationVariables
  >({
    mutationFn: (variables) =>
      gqlClient.request<
        CreateExperienceLabelMutation,
        CreateExperienceLabelMutationVariables
      >(query, variables),
    onMutate: ({ input }) => {
      cache.addLabel(labelType, input.labelId ?? '', {
        text: input.text,
        sortOrder: 0,
      });
    },
    onError: (_, { input }) => {
      cache.removeLabel(labelType, input.labelId ?? '');
    },
    onSuccess: (data, { input }) => {
      cache.updateLabel(labelType, {
        id: input.labelId ?? '',
        text: data.createExperienceLabel.text,
        sortOrder: data.createExperienceLabel.sortOrder,
      });
    },
  });

  const createLabel = useCallback(
    (
      label: Label,
      options?: MutateOptions<
        CreateExperienceLabelMutation,
        QueryError,
        CreateExperienceLabelMutationVariables
      >
    ) =>
      mutate(
        {
          input: {
            experienceId: experience.id,
            labelId: label.id,
            text: label.text,
            type: labelType,
          },
        },
        options
      ),
    [mutate, experience.id, labelType]
  );

  return { createLabel, isPending };
};
